import { useCallback, useEffect, useRef, useState } from "react";

/**
 * PAMIĘĆ POWŁOKI — jedno pobranie na sesję karty zamiast jednego na każdy montaż.
 *
 * ── PO CO TO POWSTAŁO (pomiar z produkcji, dziennik usługi, 3 dni) ──────────────────────────────
 * Powłoka aplikacji (Layout + Header) pytała serwer o te same, rzadko zmienne rzeczy po WIELE razy
 * na jedno wejście do aplikacji:
 *   /api/terms/pending 326, /api/announcements/pending 301, /api/registration/status 264,
 *   /api/languages 170, /api/ticket-modules/my 243 — przy ~140 wejściach do aplikacji.
 * Zmierzony mechanizm (test `powlokaBezKaskad.test.js`, przypadek „odświeżenie danych konta"):
 * `AuthContext.checkLoggedIn` woła `setUser` DWA RAZY na każde wejście — raz optymistycznie
 * z localStorage, drugi raz po `GET /api/users/me` (warunek `!hadSignalRToken` jest po F5 zawsze
 * prawdziwy, bo token SignalR żyje tylko w pamięci). Treść konta jest ta sama, ale OBIEKT jest nowy,
 * a efekty powłoki miały ten obiekt w tablicy zależności — więc cała powłoka leciała drugi raz.
 *
 * ── CO TO ZMIENIA, A CZEGO NIE ─────────────────────────────────────────────────────────────────
 * To jest pamięć NA CZAS ŻYCIA KARTY (zwykły moduł JS) — przeładowanie strony czyści ją w całości.
 * Nie ma tu localStorage ani sessionStorage: dane powłoki bywają zależne od konta, a pamięć
 * przeglądarki przeżywa wylogowanie i na wspólnym stanowisku pokazałaby cudze dane.
 *
 * ZASADY, KTÓRE PILNUJĄ, ŻEBY NIKT NIE ZOBACZYŁ CUDZYCH DANYCH:
 *  1. `tozsamosc` — każdy wpis zależny od konta zapisuje przy sobie identyfikator właściciela.
 *     Odczyt z inną tożsamością to PUDŁO (wpis jest kasowany i pobierany na nowo), więc po
 *     przelogowaniu na tym samym stanowisku nikt nie dostanie odpowiedzi poprzednika.
 *  2. Zmiana uprawnień (`uprawnienia-zmienione` — zdarzenie z `apiService` po cichym /refresh)
 *     kasuje CAŁĄ pamięć. Odebranie dostępu ma działać od razu, także w powłoce.
 *  3. Błędy NIE są pamiętane — nieudane sprawdzenie ma się powtórzyć przy następnej okazji.
 *  4. `ttlMs` jest siatką bezpieczeństwa dla kart otwartych cały dzień (gdyby SignalR padł).
 *     Nie jest odpytywaniem w pętli: pamięć sprawdza się tylko wtedy, gdy ktoś o nią POPROSI,
 *     czyli przy montażu powłoki — a ten zdarza się raz na wejście.
 *
 * Każdy, kto stąd czyta, MUSI mieć drogę wymuszenia świeżych danych (`uniewaznij(klucz)`), używaną
 * wszędzie tam, gdzie dotąd leciało ponowne pobranie: SignalR, akceptacja regulaminu, przeczytanie
 * komunikatu, domknięcie onboardingu.
 */

/** Klucze pamięci — wpisane w jednym miejscu, żeby dwa moduły nie rozjechały się literówką. */
export const KLUCZE_POWLOKI = {
    REGULAMIN: "powloka:regulamin-do-akceptacji",
    REJESTRACJA: "powloka:status-rejestracji",
    KOMUNIKATY: "powloka:komunikaty-oczekujace",
    JEZYKI: "powloka:jezyki-interfejsu",
    MODULY_OBSLUGI: "powloka:moduly-zgloszen-moje",
    AKCEPTACJE: "powloka:czeka-na-moja-decyzje",
    /* [G2 2026-09-19] Kafelki landingu modułu (widoczność globalna + nadania imienne) — para
       `GET /admin-menu-visibility` i `GET /admin-menu-visibility/my` dla JEDNEGO kodu strony.
       Klucz jest z sufiksem kodu strony (patrz `kluczKafelkowLandingu`), bo landing IT, landing HR
       i landing modułu dynamicznego pytają o RÓŻNE zestawy i nie wolno im się wymieszać. */
    KAFELKI_LANDINGU: "powloka:kafelki-landingu",
    /* [polecenie właściciela 2026-10-02] Czy spółka konta jest w trybie testowym powiadomień (`GET tryb-testowy-powiadomien/moj`)
       — baner nad nagłówkiem. Tożsamość = id konta; świeże dane wymusza `useTrybTestowy` (zdarzenie huba, powrót do karty). */
    TRYB_TESTOWY: "powloka:tryb-testowy-powiadomien",
};

/** Klucz pamięci dla kafelków JEDNEGO landingu (pageCode: „tickets", „hrManagement", kod modułu). */
export const kluczKafelkowLandingu = (kodStrony) => `${KLUCZE_POWLOKI.KAFELKI_LANDINGU}:${kodStrony || "?"}`;

/** Domyślna siatka bezpieczeństwa dla danych powłoki (10 minut). */
export const TTL_POWLOKI_MS = 10 * 60 * 1000;

/**
 * TOŻSAMOŚĆ KONTA DLA PAMIĘCI POWŁOKI — jedno miejsce, w którym liczy się „kto pyta".
 *
 * ⚠ DLACZEGO TO JEST OSOBNA FUNKCJA, A NIE WYRAŻENIE PRZEPISYWANE W KAŻDYM KONSUMENCIE.
 * Tożsamość jest częścią KLUCZA pamięci: dwóch konsumentów tego samego klucza, którzy policzą ją
 * choćby trochę inaczej (jeden `user.id`, drugi `user.email`), nigdy nie trafią w ten sam wpis —
 * każdy wyśle własne zapytanie i cała oszczędność znika BEZ ŻADNEGO BŁĘDU NA EKRANIE. Dokładnie
 * ta klasa rozjazdu kosztowała 243 wywołania `/api/ticket-modules/my` przy ~140 wejściach.
 * Odwrotny kierunek jest groźniejszy: gdyby któryś konsument oddał tę samą wartość dla DWÓCH
 * różnych osób (np. stałą), wpis przestałby być wiązany z człowiekiem i na wspólnym stanowisku
 * pokazałby dane poprzednika.
 *
 * `null` znaczy „nie wiem, kto pyta" — wpis jest wtedy traktowany jak dane globalne, więc wolno go
 * oddawać wyłącznie dla rzeczy niezależnych od konta (np. lista języków interfejsu).
 */
export const tozsamoscKonta = (user) => user?.id ?? user?.email ?? null;

const wpisy = new Map();

/* Zdarzenia, po których cała pamięć powłoki przestaje być wiarygodna:
   • `uprawnienia-zmienione` — cichy /refresh w `apiService` przeliczył uprawnienia; odebranie
     dostępu ma zadziałać od razu, także w powłoce,
   • `sesja-zakonczona` — `AuthContext.logout`. Sama zgodność tożsamości już nie puściłaby cudzych
     danych dalej, ale na współdzielonym stanowisku nie ma powodu, żeby odpowiedzi poprzednika
     leżały w pamięci karty choćby minutę dłużej,
   • `access-control-changed` — [G2 2026-09-19] rozgłoszona zmiana w panelu dostępu (kanał
     kanoniczny: backend → NotificationHub → `NotificationContext` → zdarzenie okna). To jest
     JEDYNE zdarzenie, które zmienia kafelki landingów, a odkąd siedzą one w tej pamięci, samo
     przeładowanie stron już nie wystarcza — bez tego wpisu administrator włączyłby kafelek,
     strony posłusznie by się odświeżyły i pokazały ZAPAMIĘTANY, stary zestaw. Kasujemy całą
     pamięć, a nie jeden klucz: zmiana dostępu potrafi ruszyć także listę modułów obsługi. */
const ZDARZENIA_KASUJACE = ["uprawnienia-zmienione", "sesja-zakonczona", "access-control-changed"];

let nasluchZalozony = false;
const zalozNasluch = () => {
    if (nasluchZalozony || typeof window === "undefined") return;
    nasluchZalozony = true;
    ZDARZENIA_KASUJACE.forEach((nazwa) => window.addEventListener(nazwa, () => wyczyscPamiecPowloki()));
};

/** Usuwa jeden wpis — następne pytanie pójdzie do serwera. */
export const uniewaznij = (klucz) => { wpisy.delete(klucz); };

/** Kasuje całą pamięć (przelogowanie, zmiana uprawnień, testy). */
export const wyczyscPamiecPowloki = () => { wpisy.clear(); };

/** Podgląd dla testów — ile wpisów i czy dany klucz jest zapamiętany. */
export const stanPamieciPowloki = () => ({
    liczbaWpisow: wpisy.size,
    klucze: [...wpisy.keys()],
});

const wpisWazny = (wpis, tozsamosc, ttlMs) => {
    if (!wpis) return false;
    if (wpis.tozsamosc !== tozsamosc) return false;
    if (wpis.wLocie) return true;                     // pobranie trwa — dołączamy się do niego
    if (ttlMs != null && Date.now() - wpis.czas > ttlMs) return false;
    return true;
};

/**
 * Zwraca dane spod klucza: z pamięci, z trwającego pobrania albo świeżo z serwera.
 *
 * @param {string} klucz         klucz z `KLUCZE_POWLOKI`
 * @param {object} opcje
 * @param {() => Promise<any>} opcje.pobieracz  wywołanie HTTP zwracające GOTOWE dane (bez `.data`)
 * @param {string|number|null} opcje.tozsamosc  identyfikator konta; `null` = dane globalne
 * @param {number|null} opcje.ttlMs             siatka bezpieczeństwa; `null` = bez wygasania
 * @param {boolean} opcje.wymus                 pomiń pamięć i pobierz na nowo (SignalR, po decyzji)
 */
export const pobierzZPamieci = (klucz, { pobieracz, tozsamosc = null, ttlMs = TTL_POWLOKI_MS, wymus = false } = {}) => {
    zalozNasluch();
    const klucznik = tozsamosc == null ? null : String(tozsamosc);
    if (wymus) wpisy.delete(klucz);
    const wpis = wpisy.get(klucz);

    if (wpisWazny(wpis, klucznik, ttlMs)) {
        return wpis.wLocie ? wpis.wLocie : Promise.resolve(wpis.dane);
    }

    const wLocie = Promise.resolve()
        .then(() => pobieracz())
        .then((dane) => {
            const biezacy = wpisy.get(klucz);
            // Ktoś w międzyczasie unieważnił wpis (np. przyszło SignalR) — nie zapisujemy
            // odpowiedzi, która była już w drodze, bo byłaby starsza niż powód unieważnienia.
            if (biezacy && biezacy.wLocie === wLocie) {
                wpisy.set(klucz, { tozsamosc: klucznik, czas: Date.now(), dane, wLocie: null });
            }
            return dane;
        })
        .catch((err) => {
            // Błędu NIE pamiętamy — inaczej jedna awaria sieci wyłączyłaby sprawdzenie na całą sesję.
            const biezacy = wpisy.get(klucz);
            if (biezacy && biezacy.wLocie === wLocie) wpisy.delete(klucz);
            throw err;
        });

    wpisy.set(klucz, { tozsamosc: klucznik, czas: Date.now(), dane: undefined, wLocie });
    return wLocie;
};

/**
 * Wygodna osłonka dla komponentu: trzyma dane w stanie, nie ustawia stanu po odmontowaniu
 * i daje `odswiez()` wymuszające pobranie z pominięciem pamięci.
 *
 * ⚠ Zamiast `AbortController`: `apiService` nie przyjmuje dziś `signal` (metody są bezargumentowe),
 * a przerwanie żądania i tak ZEPSUŁOby współdzielenie pobrania między konsumentami — drugi
 * czekający dostałby `AbortError` zamiast danych. Dlatego odcinamy SKUTEK (zapis do stanu po
 * odmontowaniu), a nie samo połączenie. Prawdziwe przerywanie wymaga zmiany w `apiService.js`
 * — dokładny opis w raporcie z tej rundy.
 */
export const usePamiecPowloki = (klucz, { pobieracz, tozsamosc = null, ttlMs = TTL_POWLOKI_MS, aktywny = true, domyslne = null } = {}) => {
    const [dane, setDane] = useState(domyslne);
    const [ladowanie, setLadowanie] = useState(!!aktywny);
    const zywy = useRef(true);
    const pobieraczRef = useRef(pobieracz);
    pobieraczRef.current = pobieracz;

    useEffect(() => {
        zywy.current = true;
        return () => { zywy.current = false; };
    }, []);

    const wczytaj = useCallback(async (wymus = false) => {
        if (!aktywny) { setLadowanie(false); return null; }
        if (wymus) uniewaznij(klucz);
        try {
            const wynik = await pobierzZPamieci(klucz, { pobieracz: () => pobieraczRef.current(), tozsamosc, ttlMs });
            if (zywy.current) { setDane(wynik); setLadowanie(false); }
            return wynik;
        } catch (err) {
            if (zywy.current) setLadowanie(false);
            return null;
        }
    }, [klucz, tozsamosc, ttlMs, aktywny]);

    useEffect(() => { wczytaj(false); }, [wczytaj]);

    return { dane, ladowanie, odswiez: () => wczytaj(true) };
};

export default usePamiecPowloki;
